import { DatetimeChangeEventDetail } from '@ionic/core';
import { IonButton, IonCol, IonDatetime, IonFooter, IonGrid, IonIcon, IonModal, IonRow } from '@ionic/react';
import { motion } from 'framer-motion';
import { calendarOutline, golfOutline, peopleOutline, personOutline, timeOutline } from 'ionicons/icons';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import GolfClubSearch, { Course } from '../GolfClubSearch';
import { GameSetup, GolfCourse } from './types';

// No longer needed as we're using GolfClubSearch component

interface GameSetupFormProps {
  initialData?: Partial<GameSetup>;
  onNext: (data: Partial<GameSetup>) => void;
}

const GameSetupForm: React.FC<GameSetupFormProps> = ({ initialData = {}, onNext }) => {
  const { t } = useTranslation();
  const [showHolesPicker, setShowHolesPicker] = useState(false);
  const [showStartingHolePicker, setShowStartingHolePicker] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showFormatPicker, setShowFormatPicker] = useState(false);

  const [gameData, setGameData] = useState<Partial<GameSetup>>({
    name: initialData.name || '',
    date: initialData.date || new Date().toISOString(),
    holes: initialData.holes || 18,
    startingHole: initialData.startingHole || 1,
    format: initialData.format || 'strokeplay',
    course: initialData.course || null,
  });



  const handleCourseSelect = (course: Course) => {
    const golfCourse: GolfCourse = {
      id: course.id.toString(),
      name: course.name,
      location: `${course.city}, ${course.country}`,
      holes: 18, // Default to 18 holes, can be changed by user
    };

    setGameData({
      ...gameData,
      course: golfCourse,
      name: gameData.name || `Game at ${course.name}`,
      holes: golfCourse.holes
    });
  };

  const handleSubmit = () => {
    onNext(gameData);
  };

  // No longer needed as we're using custom pickers

  return (
    <motion.div
      className="flex flex-col h-full p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="space-y-6 mb-8">
        {/* Course Selection */}
        <div className="space-y-2">
          <GolfClubSearch
            mode="Select"
            className="border"
            customSearchCaption={t('Search for a course')}
            onCourseSelect={handleCourseSelect}
          />
        </div>

        {/* Date and Time Pickers */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('Date & Time')}
          </label>
          <IonGrid className="p-0">
            <IonRow>
              <IonCol size="7" className="pr-1">
                <button
                  onClick={() => setShowDatePicker(true)}
                  className="w-full flex items-center justify-between p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-left"
                >
                  <span>
                    {gameData.date ? new Date(gameData.date).toLocaleDateString() : t('Select date')}
                  </span>
                  <IonIcon icon={calendarOutline} className="text-gray-400" />
                </button>
              </IonCol>
              <IonCol size="5" className="pl-1">
                <button
                  onClick={() => setShowTimePicker(true)}
                  className="w-full flex items-center justify-between p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-left"
                >
                  <span>
                    {gameData.date ? new Date(gameData.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) : t('Time')}
                  </span>
                  <IonIcon icon={timeOutline} className="text-gray-400" />
                </button>
              </IonCol>
            </IonRow>
          </IonGrid>
        </div>

        {/* Number of Holes */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('Number of Holes')}
          </label>
          <button
            onClick={() => setShowHolesPicker(true)}
            className="w-full flex items-center justify-between p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-left"
          >
            <span>{gameData.holes} {t('holes')}</span>
            <IonIcon icon={golfOutline} className="text-gray-400" />
          </button>
        </div>

        {/* Starting Hole */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('Starting Hole')}
          </label>
          <button
            onClick={() => setShowStartingHolePicker(true)}
            className="w-full flex items-center justify-between p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-left"
          >
            <span>{t('Hole')} {gameData.startingHole}</span>
            <IonIcon icon={golfOutline} className="text-gray-400" />
          </button>
        </div>

        {/* Game Format */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('Game Format')}
          </label>
          <button
            onClick={() => setShowFormatPicker(true)}
            className="w-full flex items-center justify-between p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-left"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-md bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                <IonIcon
                  icon={golfOutline}
                  className="w-full h-full p-2"
                />
              </div>
              <div className="ml-3">
                <div className="font-medium">
                  {gameData.format === 'strokeplay' && t('Stroke Play')}
                  {gameData.format === 'stableford' && t('Stableford')}
                  {gameData.format === 'matchplay' && t('Match Play')}
                  {gameData.format === 'skins' && t('Skins')}
                  {gameData.format === 'bestball' && t('Best Ball')}
                  {gameData.format === 'scramble' && t('Scramble')}
                  {gameData.format === 'alternate' && t('Alternate Shot')}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {gameData.format && ['bestball', 'scramble', 'alternate'].includes(gameData.format)
                    ? t('Team Format')
                    : t('Individual Format')}
                </div>
              </div>
            </div>
            <IonIcon icon={golfOutline} className="text-gray-400" />
          </button>
        </div>
      </div>

      <IonFooter className="mt-auto">
        <IonButton
          shape='round'
          onClick={handleSubmit}
          disabled={!gameData.course}
          className={`w-full py-4 px-6`}
        >
          <span>{t('Continue to Game Format')}</span>
        </IonButton>
      </IonFooter>

      {/* Course picker is now handled by GolfClubSearch component */}

      {/* Date Picker */}
      <IonModal isOpen={showDatePicker} onDidDismiss={() => setShowDatePicker(false)}>
        <IonDatetime
          onIonChange={(e: CustomEvent<DatetimeChangeEventDetail>) => {
            // Preserve the time part when changing date
            const currentDate = new Date(gameData.date || new Date());
            const newDate = new Date(e.detail.value as string);
            newDate.setHours(currentDate.getHours(), currentDate.getMinutes());

            setGameData({ ...gameData, date: newDate.toISOString() });
            setShowDatePicker(false);
          }}
          onIonCancel={() => setShowDatePicker(false)}
          presentation="date"
          showDefaultButtons={true}
          value={gameData.date}
          className="ios-picker"
        />
      </IonModal>

      {/* Time Picker */}
      {showTimePicker && (
        <IonDatetime
          onIonChange={(e: CustomEvent<DatetimeChangeEventDetail>) => {
            // Preserve the date part when changing time
            const currentDate = new Date(gameData.date || new Date());
            const newTime = new Date(e.detail.value as string);
            currentDate.setHours(newTime.getHours(), newTime.getMinutes());

            setGameData({ ...gameData, date: currentDate.toISOString() });
            setShowTimePicker(false);
          }}
          onIonCancel={() => setShowTimePicker(false)}
          presentation="time"
          showDefaultButtons={true}
          value={gameData.date}
          className="ios-picker"
        />
      )}

      {/* Game Format Picker */}
      <IonModal isOpen={showFormatPicker} onDidDismiss={() => setShowFormatPicker(false)}>
        <div className="p-4 h-full flex flex-col">
          <div className="flex justify-between items-center mb-4 border-b border-gray-200 dark:border-gray-700 pb-4">
            <button
              className="text-blue-500 font-medium"
              onClick={() => setShowFormatPicker(false)}
            >
              {t('Cancel')}
            </button>
            <h3 className="text-lg font-semibold">{t('Game Format')}</h3>
            <button
              className="text-blue-500 font-medium"
              onClick={() => setShowFormatPicker(false)}
            >
              {t('Done')}
            </button>
          </div>

          <div className="flex-1 overflow-auto pb-4">
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-3 flex items-center">
                <IonIcon icon={personOutline} className="mr-2 text-green-500" />
                {t('Individual Formats')}
              </h3>

              <div className="grid grid-cols-1 gap-3">
                {/* Stroke Play */}
                <div
                  onClick={() => {
                    setGameData({ ...gameData, format: 'strokeplay' });
                    setShowFormatPicker(false);
                  }}
                  className={`p-4 rounded-xl border-2 transition-all ${gameData.format === 'strokeplay'
                    ? 'border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/30'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                    }`}
                >
                  <div className="flex items-start">
                    <div className={`p-2 rounded-lg mr-3 ${gameData.format === 'strokeplay'
                      ? 'bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }`}>
                      <IonIcon icon={personOutline} className="size-5" />
                    </div>
                    <div>
                      <h4 className="font-medium">{t('Stroke Play')}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {t('Traditional golf scoring. Count total strokes for the round.')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Stableford */}
                <div
                  onClick={() => {
                    setGameData({ ...gameData, format: 'stableford' });
                    setShowFormatPicker(false);
                  }}
                  className={`p-4 rounded-xl border-2 transition-all ${gameData.format === 'stableford'
                    ? 'border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/30'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                    }`}
                >
                  <div className="flex items-start">
                    <div className={`p-2 rounded-lg mr-3 ${gameData.format === 'stableford'
                      ? 'bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }`}>
                      <IonIcon icon={personOutline} className="size-5" />
                    </div>
                    <div>
                      <h4 className="font-medium">{t('Stableford')}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {t('Points based on performance relative to par on each hole.')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Match Play */}
                <div
                  onClick={() => {
                    setGameData({ ...gameData, format: 'matchplay' });
                    setShowFormatPicker(false);
                  }}
                  className={`p-4 rounded-xl border-2 transition-all ${gameData.format === 'matchplay'
                    ? 'border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/30'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                    }`}
                >
                  <div className="flex items-start">
                    <div className={`p-2 rounded-lg mr-3 ${gameData.format === 'matchplay'
                      ? 'bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }`}>
                      <IonIcon icon={personOutline} className="size-5" />
                    </div>
                    <div>
                      <h4 className="font-medium">{t('Match Play')}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {t('Compete hole by hole against an opponent.')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Skins */}
                <div
                  onClick={() => {
                    setGameData({ ...gameData, format: 'skins' });
                    setShowFormatPicker(false);
                  }}
                  className={`p-4 rounded-xl border-2 transition-all ${gameData.format === 'skins'
                    ? 'border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/30'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                    }`}
                >
                  <div className="flex items-start">
                    <div className={`p-2 rounded-lg mr-3 ${gameData.format === 'skins'
                      ? 'bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }`}>
                      <IonIcon icon={personOutline} className="size-5" />
                    </div>
                    <div>
                      <h4 className="font-medium">{t('Skins')}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {t('Win a "skin" by having the lowest score on a hole.')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-3 flex items-center">
                <IonIcon icon={peopleOutline} className="mr-2 text-purple-500" />
                {t('Team Formats')}
              </h3>

              <div className="grid grid-cols-1 gap-3">
                {/* Best Ball */}
                <div
                  onClick={() => {
                    setGameData({ ...gameData, format: 'bestball' });
                    setShowFormatPicker(false);
                  }}
                  className={`p-4 rounded-xl border-2 transition-all ${gameData.format === 'bestball'
                    ? 'border-purple-500 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/30'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                    }`}
                >
                  <div className="flex items-start">
                    <div className={`p-2 rounded-lg mr-3 ${gameData.format === 'bestball'
                      ? 'bg-purple-100 dark:bg-purple-800 text-purple-600 dark:text-purple-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }`}>
                      <IonIcon icon={peopleOutline} className="size-5" />
                    </div>
                    <div>
                      <h4 className="font-medium">{t('Best Ball')}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {t('Teams use the best score from any team member on each hole.')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Scramble */}
                <div
                  onClick={() => {
                    setGameData({ ...gameData, format: 'scramble' });
                    setShowFormatPicker(false);
                  }}
                  className={`p-4 rounded-xl border-2 transition-all ${gameData.format === 'scramble'
                    ? 'border-purple-500 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/30'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                    }`}
                >
                  <div className="flex items-start">
                    <div className={`p-2 rounded-lg mr-3 ${gameData.format === 'scramble'
                      ? 'bg-purple-100 dark:bg-purple-800 text-purple-600 dark:text-purple-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }`}>
                      <IonIcon icon={peopleOutline} className="size-5" />
                    </div>
                    <div>
                      <h4 className="font-medium">{t('Scramble')}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {t('Teams select the best shot after each stroke.')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Alternate Shot */}
                <div
                  onClick={() => {
                    setGameData({ ...gameData, format: 'alternate' });
                    setShowFormatPicker(false);
                  }}
                  className={`p-4 rounded-xl border-2 transition-all ${gameData.format === 'alternate'
                    ? 'border-purple-500 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/30'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                    }`}
                >
                  <div className="flex items-start">
                    <div className={`p-2 rounded-lg mr-3 ${gameData.format === 'alternate'
                      ? 'bg-purple-100 dark:bg-purple-800 text-purple-600 dark:text-purple-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }`}>
                      <IonIcon icon={peopleOutline} className="size-5" />
                    </div>
                    <div>
                      <h4 className="font-medium">{t('Alternate Shot')}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {t('Team members alternate taking shots with one ball.')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </IonModal>

      {/* Holes Picker */}
      {showHolesPicker && (
        <div className="picker-wrapper">
          {/* Using a custom modal instead of IonPicker due to TypeScript compatibility issues */}
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
            <div className="bg-white dark:bg-gray-900 rounded-t-2xl w-full max-w-md">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <button
                  className="text-blue-500 font-medium"
                  onClick={() => setShowHolesPicker(false)}
                >
                  {t('Cancel')}
                </button>
                <h3 className="text-center font-medium">{t('Number of Holes')}</h3>
                <button
                  className="text-blue-500 font-medium"
                  onClick={() => {
                    // Keep current value
                    setShowHolesPicker(false);
                  }}
                >
                  {t('Done')}
                </button>
              </div>
              <div className="p-4 flex justify-center">
                <div className="grid grid-cols-2 gap-4 w-full">
                  <button
                    className={`p-4 rounded-xl text-center ${gameData.holes === 9
                      ? 'bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-500 font-medium'
                      : 'bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600'}`}
                    onClick={() => {
                      setGameData({ ...gameData, holes: 9 });
                      setShowHolesPicker(false);
                    }}
                  >
                    9 {t('holes')}
                  </button>
                  <button
                    className={`p-4 rounded-xl text-center ${gameData.holes === 18
                      ? 'bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-500 font-medium'
                      : 'bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600'}`}
                    onClick={() => {
                      setGameData({ ...gameData, holes: 18 });
                      setShowHolesPicker(false);
                    }}
                  >
                    18 {t('holes')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Starting Hole Picker */}
      {showStartingHolePicker && (
        <div className="picker-wrapper">
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
            <div className="bg-white dark:bg-gray-900 rounded-t-2xl w-full max-w-md">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <button
                  className="text-blue-500 font-medium"
                  onClick={() => setShowStartingHolePicker(false)}
                >
                  {t('Cancel')}
                </button>
                <h3 className="text-center font-medium">{t('Starting Hole')}</h3>
                <button
                  className="text-blue-500 font-medium"
                  onClick={() => {
                    // Keep current value
                    setShowStartingHolePicker(false);
                  }}
                >
                  {t('Done')}
                </button>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-3 gap-3">
                  {Array.from({ length: gameData.holes || 18 }, (_, i) => i + 1).map(hole => (
                    <button
                      key={hole}
                      className={`p-3 rounded-xl text-center ${gameData.startingHole === hole
                        ? 'bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-500 font-medium'
                        : 'bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600'}`}
                      onClick={() => {
                        setGameData({ ...gameData, startingHole: hole });
                        setShowStartingHolePicker(false);
                      }}
                    >
                      {hole}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default GameSetupForm;
